import { apiFetch } from './api';

/**
 * Phase 18 frontend mirror of the Phase 17 automation CRUD API. Only the
 * executable vocabulary is typed: any value outside these unions is invalid
 * by construction (backend remains authoritative).
 */
export type AutomationTriggerType = 'comment' | 'dm' | 'story_reply';
export type AutomationMatchType = 'exact' | 'contains' | 'starts_with' | 'ends_with';
export type AutomationActionType = 'reply_comment' | 'private_reply' | 'send_dm_text';

export interface AutomationTrigger {
  type: AutomationTriggerType;
  match_type: AutomationMatchType;
  keywords: string[];
  media_id: string | null;
}

export interface AutomationAction {
  action_type: AutomationActionType;
  text: string;
  action_order: number;
  enabled: boolean;
}

/** Safe management fields only — no tokens, no secrets, no execution data. */
export interface ManagedAutomation {
  id: string;
  instagram_account_id: string;
  name: string;
  enabled: boolean;
  priority: number;
  trigger: AutomationTrigger;
  actions: AutomationAction[];
  created_at: number;
  updated_at: number;
}

export interface AutomationTriggerPayload {
  type: AutomationTriggerType;
  match_type: AutomationMatchType;
  keywords: string[];
  media_id?: string;
}

export interface AutomationActionPayload {
  action_type: AutomationActionType;
  text: string;
}

/**
 * Exactly the Phase 17 create schema fields — never `enabled` via PATCH,
 * never unknown keys: the backend rejects strict-object extras with 400.
 */
export interface CreateAutomationPayload {
  instagram_account_id: string;
  name: string;
  priority?: number;
  trigger: AutomationTriggerPayload;
  actions: AutomationActionPayload[];
}

export interface AutomationListResponse {
  automations: ManagedAutomation[];
  limit: number;
  offset: number;
  total: number;
}

export interface AutomationDetailResponse {
  automation: ManagedAutomation;
}

export interface AutomationDeleteResponse {
  success: boolean;
  id: string;
}

export function listAutomationsRequest(
  limit: number,
  offset: number,
): Promise<AutomationListResponse> {
  return apiFetch<AutomationListResponse>(`/api/automations?limit=${limit}&offset=${offset}`);
}

export function getAutomationRequest(id: string): Promise<AutomationDetailResponse> {
  return apiFetch<AutomationDetailResponse>(`/api/automations/${encodeURIComponent(id)}`);
}

export function createAutomationRequest(
  payload: CreateAutomationPayload,
): Promise<AutomationDetailResponse> {
  return apiFetch<AutomationDetailResponse>('/api/automations', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export function updateAutomationRequest(
  id: string,
  payload: CreateAutomationPayload,
): Promise<AutomationDetailResponse> {
  return apiFetch<AutomationDetailResponse>(`/api/automations/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: JSON.stringify(payload),
  });
}

export function enableAutomationRequest(id: string): Promise<AutomationDetailResponse> {
  return apiFetch<AutomationDetailResponse>(`/api/automations/${encodeURIComponent(id)}/enable`, {
    method: 'POST',
  });
}

export function disableAutomationRequest(id: string): Promise<AutomationDetailResponse> {
  return apiFetch<AutomationDetailResponse>(`/api/automations/${encodeURIComponent(id)}/disable`, {
    method: 'POST',
  });
}

export function deleteAutomationRequest(id: string): Promise<AutomationDeleteResponse> {
  return apiFetch<AutomationDeleteResponse>(`/api/automations/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  });
}
